import type { ReactElement } from "react"

import type { SliderProps } from "@mui/material"
import { Slider } from "@mui/material"

import type { BaseSliderProps, SliderVariant } from "../Interfaces"

const baseSliderStyles = {
  "&.MuiSlider-root": { marginBottom: 0 },
  "&.Mui-disabled": {
    ".MuiSlider-thumb": {
      backgroundColor: "gray",
      width: "6px",
      height: "16px",
      borderRadius: "4px",
    },
  },
  ".MuiSlider-markLabel": {
    fontSize: "12px",
    fontWeight: 400,
    color: "var(--base-text-2)",
    fontFamily: "Inter, sans-serif",
    top: "-10px",
  },
}

const positiveSliderStyles = {
  "& .MuiSlider-valueLabel": {
    fontSize: 12,
    fontWeight: "normal",
    top: -6,
    backgroundColor: "unset",
    transform: "translateY(-70%) scale(1) !important",
    "&::before": {
      display: "none",
    },
    "& *": {
      background: "transparent",
      color: "var(--blue-text-2)",
    },
  },
  "& .MuiSlider-rail": {
    background: "var(--blue-text-2)",
    border: "none",
  },
  "& .MuiSlider-track": {
    minHeight: 4,
    background: "var(--gray-background-2)",
    border: "none",
  },
  "& .MuiSlider-thumb": {
    background: "var(--blue-text-2)",
    width: 12,
    height: 12,
  },
}

const negativeSliderStyles = {
  "& .MuiSlider-valueLabel": {
    fontSize: 12,
    fontWeight: "normal",
    top: -6,
    backgroundColor: "unset",
    transform: "translateY(-70%) scale(1) !important",
    "&::before": {
      display: "none",
    },
    "& *": {
      background: "transparent",
      color: "var(--red-background-1)",
    },
  },
  "& .MuiSlider-rail": {
    minHeight: 4,
    background: "var(--gray-background-2)",
    opacity: 1,
    border: "none",
  },
  "& .MuiSlider-track": {
    background: "var(--red-background-1)",
    border: "none",
  },
  "& .MuiSlider-thumb": {
    background: "var(--red-background-1)",
    width: 12,
    height: 12,
  },
}

const singleSliderStyles = {
  "& .MuiSlider-valueLabel": {
    fontSize: 12,
    fontWeight: "normal",
    top: -6,
    backgroundColor: "unset",
    transform: "translateY(-70%) scale(1) !important",
    "&::before": {
      display: "none",
    },
    "& *": {
      background: "transparent",
      color: "var(--blue-text-2)",
    },
  },
  "& .MuiSlider-rail": {
    minHeight: 4,
    background: "var(--gray-background-2)",
    border: "none",
  },
  "& .MuiSlider-track": {
    background: "var(--blue-text-2)",
    border: "none",
  },
  "& .MuiSlider-thumb": {
    background: "var(--blue-text-2)",
    width: 12,
    height: 12,
  },
}

const SLIDER_VARIANT_PROPS_MAPPER: Record<SliderVariant, SliderProps> = {
  positive: {
    id: "positive",
    name: "positive",
    track: "inverted",
    "aria-labelledby": "positive-range-slider",
    sx: {
      ...baseSliderStyles,
      ...positiveSliderStyles,
    },
  },
  negative: {
    "aria-labelledby": "negative-range-slider",
    id: "negative",
    name: "negative",
    sx: { ...baseSliderStyles, ...negativeSliderStyles },
  },
  single: {
    "aria-labelledby": "single-range-slider",
    id: "single_slider",
    name: "single_slider",
    sx: { ...baseSliderStyles, ...singleSliderStyles },
  },
} as const

export function BaseSlider(props: Readonly<BaseSliderProps>): ReactElement {
  const { variant, sliderProps } = props

  return (
    <Slider
      {...SLIDER_VARIANT_PROPS_MAPPER[variant]}
      disableSwap
      valueLabelDisplay="on"
      getAriaValueText={(value) => `${value}%`}
      {...sliderProps}
    />
  )
}
